// Static content / mock data for marketing site.
import {
  HardHat, Mountain, Droplet, Radio, Building2, Hospital, Tractor, Plane,
  Factory, Shield, Tv, Server, Wind, Hotel, Sparkles, Truck, Zap, Sun,
  BatteryCharging, Cpu, Wifi, Cloud, Activity, ShieldCheck, Globe, Award,
} from "lucide-react";

export const STATS = [
  { label: "24/7 Power", value: "ALWAYS-ON" },
  { label: "Zero Noise", value: "<35 dB" },
  { label: "Zero Diesel", value: "0 LITRES" },
  { label: "Fast Deployment", value: "<4 HRS" },
  { label: "Clean Energy", value: "100%" },
  { label: "Worldwide", value: "18+ COUNTRIES" },
];

export const TRUSTED_BY = [
  "GOV.UAE", "ARAMCO", "BECHTEL", "ETISALAT", "DP WORLD",
  "SIEMENS ENERGY", "VEOLIA", "TOTAL", "ENGIE", "EMIRATES",
  "STC", "RIO TINTO", "EDF", "BP ENERGY",
];

export const PROBLEM_COMPARISON = {
  diesel: [
    "$0.50–$0.70 / kWh fuel + logistics",
    "85–110 dB constant noise",
    "2.7 kg CO₂ per litre burnt",
    "Weekly service & filter changes",
    "Fuel theft & pilferage",
    "NOx, SOx, particulate pollution",
  ],
  mbess: [
    "<$0.18 / kWh delivered electricity",
    "<35 dB virtually silent",
    "Zero on-site emissions",
    "Remote OTA monitoring",
    "Locked, IoT-tracked containers",
    "Solar / grid compatible recharge",
  ],
};

export const MBESS_MODELS = [
  {
    name: "MBESS-106",
    capacity: "106 kWh",
    peak: "80 kW",
    continuous: "50 kW",
    voltage: "403.2 – 525.6 V",
    outputAC: "230 / 400 V · 50/60 Hz",
    cell: "LFP 230 Ah · 1P144S",
    weight: "1,980 kg",
    dims: "2100 × 1310 × 1550 mm",
    footprint: "2.75 m²",
    apps: "Events, EV Rescue, Film, Telecom",
    image: "/products/mbess-106.jpg",
    datasheet: "/downloads/datasheets/mbess-106.pdf",
  },
  {
    name: "MBESS-261",
    capacity: "261 kWh",
    peak: "120 kW",
    continuous: "105 kW",
    voltage: "728 – 949 V",
    outputAC: "230 / 400 V · 50/60 Hz",
    cell: "LFP · 1P260S",
    weight: "3,440 kg",
    dims: "2928 × 1497 × 1655 mm",
    footprint: "4.38 m²",
    apps: "Construction, Utilities, EV Charging",
    image: "/products/mbess-261.jpg",
    datasheet: "/downloads/datasheets/mbess-261.pdf",
  },
  {
    name: "MBESS-431",
    capacity: "431 kWh",
    peak: "320 kW",
    continuous: "105 kW",
    voltage: "582.4 – 759.2 V",
    outputAC: "400 V · 50/60 Hz",
    cell: "LFP 324 Ah · 2P208S",
    weight: "5,990 kg",
    dims: "3205 × 1740 × 2117 mm",
    footprint: "5.57 m²",
    apps: "Construction, Mining, Grid Support",
    image: "/products/mbess-431.jpg",
    datasheet: "/downloads/datasheets/mbess-431.pdf",
  },
  {
    name: "MBESS-627",
    capacity: "627 kWh",
    peak: "320 kW",
    continuous: "210 kW",
    voltage: "582.4 – 759.2 V",
    outputAC: "400 V · 50/60 Hz",
    cell: "LFP 314 Ah",
    weight: "7,690 kg",
    dims: "3205 × 1740 × 2117 mm",
    footprint: "5.57 m²",
    apps: "Mining, Industrial, Microgrids",
    image: "/products/mbess-627.jpg",
    datasheet: "/downloads/datasheets/mbess-627.pdf",
  },
  {
    name: "MBESS-863",
    capacity: "863 kWh",
    peak: "640 kW",
    continuous: "210 kW",
    voltage: "582.4 – 759.2 V",
    outputAC: "400 V · 50/60 Hz",
    cell: "LFP 324 Ah · 2P208S ×2",
    weight: "11,800 kg",
    dims: "4508 × 2440 × 2591 mm",
    footprint: "11.00 m²",
    apps: "Utility-scale, Data Centers, EaaS",
    image: "/products/mbess-863.jpg",
    datasheet: "/downloads/datasheets/mbess-863.pdf",
  },
  {
    name: "MBESS-1290",
    capacity: "1.29 MWh",
    peak: "1.2 MW",
    continuous: "420 kW",
    voltage: "582.4 – 759.2 V",
    outputAC: "400 V · 50/60 Hz",
    cell: "LFP 324 Ah · 3P208S ×2",
    weight: "14,800 kg",
    dims: "4508 × 2440 × 2691 mm",
    footprint: "11.00 m²",
    apps: "Utility Microgrids, Mining, Substation Backup",
    image: "/products/mbess-1290.jpg",
    datasheet: "/downloads/datasheets/mbess-1290.pdf",
  },
];

export const FEATURES = [
  { icon: BatteryCharging, label: "Fast Charging" },
  { icon: Cpu, label: "AI Energy Management" },
  { icon: Wifi, label: "Remote Monitoring" },
  { icon: Sun, label: "Solar Compatible" },
  { icon: Zap, label: "Grid Compatible" },
  { icon: Truck, label: "Generator Compatible" },
  { icon: Activity, label: "EV Charging Ready" },
  { icon: Cloud, label: "Weather Resistant IP54" },
];

export const APPLICATIONS = [
  { icon: HardHat, label: "Construction", image: "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=900&q=80" },
  { icon: Mountain, label: "Mining", image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=900&q=80" },
  { icon: Hotel, label: "Remote Villages", image: "https://images.unsplash.com/photo-1473773508845-188df298d2d1?w=900&q=80" },
  { icon: ShieldCheck, label: "Emergency Backup", image: "https://images.unsplash.com/photo-1599058917212-d750089bc07e?w=900&q=80" },
  { icon: Hospital, label: "Hospitals", image: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=900&q=80" },
  { icon: Radio, label: "Telecom Towers", image: "https://images.unsplash.com/photo-1573164574511-73c773193279?w=900&q=80" },
  { icon: Sparkles, label: "Events", image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=900&q=80" },
  { icon: BatteryCharging, label: "EV Charging", image: "https://images.pexels.com/photos/5864153/pexels-photo-5864153.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { icon: Shield, label: "Military", image: "https://images.unsplash.com/photo-1473445730015-841f29a9490b?w=900&q=80" },
  { icon: Tractor, label: "Agriculture", image: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=900&q=80" },
  { icon: Tv, label: "Film Industry", image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=900&q=80" },
  { icon: Server, label: "Data Centers", image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=900&q=80" },
  { icon: Factory, label: "Utilities", image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=900&q=80" },
];

export const PRODUCTS = [
  {
    name: "MBESS-106",
    desc: "Compact 106 kWh / 80 kW plug-and-play Mobile BESS & Charging Station. Trolley-mounted, IP54, virtually silent.",
    image: "/products/mbess-106.jpg",
    datasheet: "/downloads/datasheets/mbess-106.pdf",
    specs: ["106 kWh", "80 kW peak", "IP54", "LFP · Liquid-cooled"],
  },
  {
    name: "MBESS-261",
    desc: "261 kWh / 120 kW trailer-mounted Mobile BESS & Charging Station. Road-towable microgrid for construction & remote sites.",
    image: "/products/mbess-261.jpg",
    datasheet: "/downloads/datasheets/mbess-261.pdf",
    specs: ["261 kWh", "120 kW peak", "Dual-gun DC", "Road-towable"],
  },
  {
    name: "MBESS-431",
    desc: "431 kWh / 320 kW trailer-mounted Mobile BESS. High-power charging & site electrification for mining and heavy industry.",
    image: "/products/mbess-431.jpg",
    datasheet: "/downloads/datasheets/mbess-431.pdf",
    specs: ["431 kWh", "320 kW peak", "DC fast · Dual gun", "400 V AC"],
  },
  {
    name: "MBESS-627",
    desc: "627 kWh / 320 kW extended-runtime Mobile BESS & Charging Station. Multi-shift power delivery, remote monitoring built-in.",
    image: "/products/mbess-627.jpg",
    datasheet: "/downloads/datasheets/mbess-627.pdf",
    specs: ["627 kWh", "320 kW peak", "210 kW cont.", "IoT telemetry"],
  },
  {
    name: "MBESS-863",
    desc: "863 kWh / 640 kW containerised utility-scale Mobile BESS & Charging Station. Peak shaving, ancillary services, EaaS-ready.",
    image: "/products/mbess-863.jpg",
    datasheet: "/downloads/datasheets/mbess-863.pdf",
    specs: ["863 kWh", "640 kW peak", "Containerised", "FCAS ready"],
  },
  {
    name: "MBESS-1290",
    desc: "1.29 MWh / 1.2 MW flagship containerised Mobile BESS & Charging Station. Utility-grade capacity for microgrids, mining and substation backup.",
    image: "/products/mbess-1290.jpg",
    datasheet: "/downloads/datasheets/mbess-1290.pdf",
    specs: ["1.29 MWh", "1.2 MW peak", "420 kW cont.", "Grid-tie"],
  },
];

export const INDUSTRIES = [
  { label: "Construction", icon: HardHat, image: "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=1200&q=80", copy: "Eliminate diesel logistics on greenfield sites. Power cranes, welders & site offices silently." },
  { label: "Mining", icon: Mountain, image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1200&q=80", copy: "Cut underground ventilation costs and meet ESG targets with zero-emission haulage support." },
  { label: "Oil & Gas", icon: Droplet, image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1200&q=80", copy: "Replace flare-powered camps with clean MBESS. Lower OPEX & methane intensity scores." },
  { label: "Government", icon: Award, image: "https://images.unsplash.com/photo-1473445730015-841f29a9490b?w=1200&q=80", copy: "Deliver rural electrification & disaster relief power without grid extension capex." },
  { label: "Military", icon: Shield, image: "https://images.unsplash.com/photo-1473445730015-841f29a9490b?w=1200&q=80", copy: "Silent, signature-free power for forward operating bases. Solar-rechargeable in field." },
  { label: "Hospitality", icon: Hotel, image: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1200&q=80", copy: "Power remote luxury resorts & glamping camps with zero-noise, zero-diesel electricity." },
  { label: "Manufacturing", icon: Factory, image: "https://images.unsplash.com/photo-1581090700227-1e37b190418e?w=1200&q=80", copy: "Peak shave, demand response, and provide backup with grid-parallel MBESS." },
  { label: "Utilities", icon: Zap, image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1200&q=80", copy: "Frequency response, black-start support & temporary substation replacement." },
  { label: "Telecom", icon: Radio, image: "https://images.unsplash.com/photo-1573164574511-73c773193279?w=1200&q=80", copy: "Replace tower diesel gensets with solar-MBESS hybrids. Cut OPEX by up to 70%." },
  { label: "Agriculture", icon: Tractor, image: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=1200&q=80", copy: "Power irrigation pumps, cold storage and processing where the grid does not reach." },
];

export const WHY_TABLE = [
  { metric: "Fuel Cost", tratou: "$0 fuel", diesel: "$0.50–$0.70 / kWh", grid: "Capex + 24mo wait" },
  { metric: "Carbon", tratou: "0 g CO₂ on-site", diesel: "2.7 kg / litre", grid: "Varies by mix" },
  { metric: "Noise", tratou: "<35 dB", diesel: "85–110 dB", grid: "n/a" },
  { metric: "Deployment", tratou: "<4 hours", diesel: "1–3 days", grid: "12–36 months" },
  { metric: "Maintenance", tratou: "Remote OTA", diesel: "Weekly service", grid: "Utility owned" },
  { metric: "Mobility", tratou: "Trailer / container", diesel: "Skid mounted", grid: "Fixed" },
  { metric: "Scalability", tratou: "Modular stacking", diesel: "Linear cost", grid: "Capex bound" },
  { metric: "Smart Monitoring", tratou: "Yes — full IoT", diesel: "Manual", grid: "Limited" },
];

export const DEPLOY_LOCATIONS = [
  { region: "Middle East", x: "59%", y: "42%" },
  { region: "Africa", x: "52%", y: "58%" },
  { region: "India", x: "70%", y: "47%" },
  { region: "Southeast Asia", x: "78%", y: "55%" },
  { region: "Europe", x: "50%", y: "30%" },
  { region: "UAE HQ", x: "61%", y: "44%" },
];

export const TESTIMONIALS = [
  { quote: "We replaced four diesel gensets with two MBESS units. Fuel logistics gone, noise complaints gone, ESG scorecard transformed.", author: "Operations Director", company: "Tier-1 Construction Contractor" },
  { quote: "Tratou deployed power for our remote festival in under 6 hours. 18,000 attendees, zero diesel, zero generator hum.", author: "Production Lead", company: "Regional Events Group" },
  { quote: "Their Energy-as-a-Service model removed $1.2M of capex from our balance sheet. We just pay per kWh delivered.", author: "CFO", company: "Telecom Tower Operator" },
];

export const INVESTOR_HIGHLIGHTS = [
  { k: "Addressable Market", v: "$280B by 2030" },
  { k: "CAGR (MBESS sector)", v: "32%" },
  { k: "Diesel Replacement TAM", v: "1.4 TWh / yr" },
  { k: "Pilot Sites", v: "12 (UAE + KSA)" },
  { k: "Series A Target", v: "$25M" },
  { k: "Use of Funds", v: "Fleet expansion + R&D" },
];

export const ROADMAP = [
  { year: "2025 Q1", milestone: "Pilot deployments — UAE construction & events" },
  { year: "2025 Q3", milestone: "EaaS commercial launch — 25 MBESS units in operation" },
  { year: "2026 Q2", milestone: "Series A close, expansion into KSA, Egypt, India" },
  { year: "2026 Q4", milestone: "Solar-MBESS hybrid product line release" },
  { year: "2027", milestone: "1 GWh of mobile storage under management" },
];

export const CAREERS = [
  { title: "Senior Power Electronics Engineer", location: "Dubai, UAE", type: "Full-time" },
  { title: "Field Operations Manager — MBESS Fleet", location: "Abu Dhabi, UAE", type: "Full-time" },
  { title: "Software Engineer — IoT & Telemetry", location: "Remote (GMT+4 overlap)", type: "Full-time" },
  { title: "Business Development — Mining & Oil & Gas", location: "Riyadh, KSA", type: "Full-time" },
  { title: "Battery Cell Test Engineer", location: "Dubai, UAE", type: "Full-time" },
];

export const INDUSTRIES_NAV = INDUSTRIES.map((i) => i.label);

// Extended options for the lead-capture Industry dropdown (superset of Industries page cards).
export const INDUSTRY_OPTIONS = [
  "Agriculture", "Construction", "Disaster Relief", "Events", "EV Charging Station",
  "EV Rescue", "Government", "Hospitality", "Hospitals", "Manufacturing", "Military",
  "Mining", "NGOs", "Oil & Gas", "Schools", "Telecom", "Tourism", "Utilities", "Others",
];

// ISO 3166 country names for the lead-capture Country dropdown (alphabetical).
export const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina",
  "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados",
  "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana",
  "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon",
  "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros",
  "Congo (Brazzaville)", "Congo (Kinshasa)", "Costa Rica", "Côte d'Ivoire", "Croatia", "Cuba",
  "Cyprus", "Czechia", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt",
  "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland",
  "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala",
  "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hong Kong", "Hungary", "Iceland",
  "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
  "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Macao", "Madagascar",
  "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius",
  "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique",
  "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger",
  "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine",
  "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
  "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines",
  "Samoa", "San Marino", "São Tomé and Príncipe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles",
  "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa",
  "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland",
  "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga",
  "Trinidad and Tobago", "Tunisia", "Türkiye", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine",
  "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu",
  "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe", "Other",
];
